import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useCallback, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { FormInput } from "../../../components/FormInput";
import FormNumberInput from "../../../components/FormNumberInput";
import NoInfo from "../../../components/NoInfo";
import PrimeTableFooter from "../../../components/PrimeTableFooter";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import { B2050111Before, queryGwpList, updateGwp } from "../../../utility/service/emission-factor-service";
import { checkDpSuccess } from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";

const GWP = () => {
  const { t } = useTranslation();
  const [, loading] = useContext(SystemLoading);
  const [, dispatch] = useContext(MessageContext);
  const [lastSeq, setLastSeq] = useState("");
  const [data, setData] = useState([]);
  const [itemObj, setItemObj] = useState({});
  const [DetailDialog, setDetailDialog] = useState(false);
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const isEditable = useEditableFlag("B20501");

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    resetField,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  useEffect(() => {
    axios_queryGwpList();
    axios_B2050111Before();
  }, []);

  const axios_queryGwpList = (reqBody = {}, readMore = false) => {
    checkLoading(loading);
    queryGwpList(reqBody)
      .then((res) => {
        const data = res.data.RespBody.gwpList;
        if (data.length > 0) {
          setLastSeq(data[data.length - 1].gwpSeq);
          setData((prev) => (readMore ? [...prev, ...data] : [...data]));
        } else if (!readMore) {
          setData([]);
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const axios_B2050111Before = () => {
    checkLoading(loading);
    B2050111Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule]
  );

  //避免觸發 按enter 後 就觸發 onSubmit 事件冒泡 給input 使用 <FormInput enterClick事件 ; 一般input onKeyDown事件
  const inputCheckKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  // 查看更多
  const btnReadMore = () => {
    let reqBody = {
      gwpSeq: lastSeq,
      keywordSearch: getValues("keywordSearch"),
    };
    axios_queryGwpList(reqBody, true);
  };

  // 查詢
  const onSubmit = (data) => {
    axios_queryGwpList({ keywordSearch: data.keywordSearch });
  };

  const actionBodyTemplate = (rowData, props) => {
    if (rowData) {
      return (
        <Button
          tooltip={t(`button.detail`)}
          tooltipOptions={{ position: "top" }}
          rounded
          text
          severity="secondary"
          className="p-button-icon-only"
          onClick={() => {
            actionItem(rowData, "detail");
          }}
        >
          <FontAwesomeIcon icon="fa-solid fa-eye" />
        </Button>
      );
    }
  };

  const actionItem = (data = {}, action = "detail") => {
    if (action === "detail") {
      setValue("gwpCode", data.gwpCode ?? "");
      setValue("gwpName", data.gwpName ?? "");
      setValue("gwpNameEnUs", data.gwpNameEnUs ?? "");
      setValue("gwpNameLocale2", data.gwpNameLocale2 ?? "");
      setValue("ar5", data.ar5 ?? "");
      setValue("ar6", data.ar6 ?? "");
      setItemObj(data);
      setDetailDialog(true);
      clearErrors();
    }
  };

  const defaultEdtiGwp = () => {
    setValue("gwpCode", "");
    setValue("gwpName", "");
    setValue("gwpNameEnUs", "");
    setValue("gwpNameLocale2", "");
    setValue("ar5", "");
    setValue("ar6", "");
    setItemObj({});
    clearErrors();
  };

  // 更新
  const onEditSubmit = (data) => {
    checkLoading(loading);
    const reqBody = {
      gwpSeq: itemObj?.gwpSeq,
      ar5: data.ar5,
      ar6: data.ar6,
    };
    updateGwp(reqBody)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          axios_queryGwpList();
        }
      })
      .finally(() => {
        checkLoading(loading, false);
        setDetailDialog(false);
        defaultEdtiGwp();
      });
  };

  return (
    <div>
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid">
              <div className="col-12">
                <FormInput
                  register={register}
                  errors={errors}
                  id="keywordSearch"
                  type="text"
                  labelClass={`control-label`}
                  enterClick={inputCheckKeyDown}
                  labelText={t("keyword")}
                />
              </div>
            </div>
            <div className="col-12 gap-2 flex-all-center">
              <Button type="submit" label={t("button.query")} />
            </div>
          </form>
        </div>
      </div>
      <div className="p-card mt-4">
        <div className="p-card-body">
          <DataTable
            stripedRows
            resizableColumns
            className="white-data-table hover-highlight no-shadow"
            emptyMessage={<NoInfo panel="table" />}
            value={data}
            footer={
              data?.length > 0 && <PrimeTableFooter rowDataList={data} isShowMore={true} btnReadMore={btnReadMore} />
            }
          >
            <Column field="sourceCode" header={t("emissionCode")} />
            <Column field="ghgChemicalFormula" header={t("gasCode")} />
            <Column field="gwpCode" header={t("gwpCode")} />
            <Column field="ar5" header={"AR5"} headerClassName="right-header" bodyClassName="text-right" />
            <Column field="ar6" header={"AR6"} headerClassName="right-header" bodyClassName="text-right" />
            <Column header={t("action_type")} body={actionBodyTemplate}></Column>
          </DataTable>
        </div>
      </div>

      {/* 詳細頁/編輯 */}
      <PrimeDialog
        size="md"
        visible={DetailDialog}
        onClose={() => {
          setDetailDialog(false);
          defaultEdtiGwp();
        }}
        disabledConfirm={!isEditable}
        onConfirm={handleSubmit(onEditSubmit)}
        title={t("button.detail")}
        bodyTemplate={
          <form>
            <div className="grid">
              {/* 氣體代碼 */}
              <div className="col-12">
                <FormInput
                  disabled={true}
                  register={register}
                  errors={errors}
                  id="gwpCode"
                  name="gwpCode"
                  type="text"
                  labelText={t("emissionCode")}
                  rules={setValidForm("gwpCode")}
                />
              </div>
              {/* 氣體名稱(zh-TW) */}
              <div className="col-12">
                <FormInput
                  disabled={true}
                  register={register}
                  errors={errors}
                  id="gwpName"
                  name="gwpName"
                  type="text"
                  labelText={`${t("gasCode")}(zh-TW)`}
                  rules={setValidForm("gwpName")}
                />
              </div>
              {/* 氣體名稱(en-US) */}
              <div className="col-12">
                <FormInput
                  disabled={true}
                  register={register}
                  errors={errors}
                  id="gwpNameEnUs"
                  name="gwpNameEnUs"
                  type="text"
                  labelText={`${t("gasCode")}(en-US)`}
                  rules={setValidForm("gwpNameEnUs")}
                />
              </div>
              {/* 氣體名稱(vi-VN) */}
              <div className="col-12">
                <FormInput
                  disabled={true}
                  register={register}
                  errors={errors}
                  id="gwpNameLocale2"
                  name="gwpNameLocale2"
                  type="text"
                  labelText={`${t("gasCode")}(vi-VN)`}
                  rules={setValidForm("gwpNameLocale2")}
                />
              </div>
              {/* AR5 */}
              <div className="col-12">
                <FormNumberInput
                  disabled={!isEditable}
                  register={register}
                  errors={errors}
                  id="ar5"
                  name="ar5"
                  type="text"
                  labelText={`AR5`}
                  value={watch("ar5")}
                  onChange={(newValue) => setValue("ar5", newValue)}
                />
              </div>
              {/* AR6 */}
              <div className="col-12">
                <FormNumberInput
                  disabled={!isEditable}
                  register={register}
                  errors={errors}
                  id="ar6"
                  name="ar6"
                  type="text"
                  labelText={`AR6`}
                  value={watch("ar6")}
                  onChange={(newValue) => setValue("ar6", newValue)}
                />
              </div>
            </div>
          </form>
        }
      />
    </div>
  );
};

export default GWP;
